import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class HWMarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const isNoRepeat = this.hasAttribute('data-no-repeat');
    const isDisabled = this.hasAttribute('data-disabled');

    if (isDisabled) {
      // No animation — just render items statically
      this.#setSpeed(0);
      return;
    }

    if (this.#hasSsrLoop()) {
      this.#setSpeed(this.#calculateSpeed());
      window.addEventListener('resize', this.#handleResize);
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
      return;
    }

    if (!this.#isAlwaysLoopTrack()) {
      // Default: static when items fit — scrolling an under-filled track looks broken.
      const contentFits = await this.#checkContentFits();
      if (contentFits) {
        this.#setSpeed(0);
        this.setAttribute('data-disabled', '');
        window.addEventListener('resize', this.#handleResize);
        return;
      }
    }

    if (!isNoRepeat) {
      const { numberOfCopies } = await this.#queryNumberOfCopies();
      this.#stripInTrackCopies();
      this.#addRepeatedItems(numberOfCopies);

      this.setAttribute('data-scrolling', '');
      this.#duplicateContent();
      this.#setSpeed(this.#calculateSpeed());
      await this.#refreshMarqueeAfterLayout();
    }

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  /**
   * @type {NodeListOf<HTMLElement> | null}
   */
  #overlays = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    if (value === 0) {
      // Static mode — no animation
      this.style.removeProperty('--marquee-speed');
      return;
    }
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  /** Merchant opted in via “Always loop track” — never stop for content that fits. */
  #isAlwaysLoopTrack() {
    return this.hasAttribute('data-always-loop-track');
  }

  /** Loop duplicate + tiles rendered in Liquid — no post-load DOM changes. */
  #hasSsrLoop() {
    return this.hasAttribute('data-ssr-loop');
  }

  /** Reset to one .marquee__repeated-items before re-tiling to the scroll lane width. */
  #stripInTrackCopies() {
    const { content } = this.refs;
    while (content.children.length > 1) {
      content.lastElementChild?.remove();
    }
  }

  /**
   * Returns true if the items fit within the container without scrolling.
   */
  async #checkContentFits() {
    const { marqueeItems } = this.refs;
    // Split: available width = full marquee minus static overlays (same fit test as scrolling
    // but subtract overlay widths so we compare against the scroll lane).
    if (!this.#overlays) {
      this.#overlays = this.querySelectorAll('.marquee__static-overlay');
    }
    return new Promise((resolve) => {
      if (!marqueeItems[0]) return resolve(true);
      const observer = new IntersectionObserver((entries) => {
        observer.disconnect();
        const entry = entries[0];
        if (!entry) return resolve(true);
        const containerWidth = entry.rootBounds?.width ?? 0;
        let totalOverlayWidth = 0;
        this.#overlays.forEach((overlay) => {
          totalOverlayWidth += overlay.getBoundingClientRect().width;
        });
        const availableWidth = containerWidth - totalOverlayWidth;
        const contentWidth = entry.boundingClientRect.width;
        resolve(contentWidth <= availableWidth);
      }, { root: this });
      observer.observe(marqueeItems[0]);
    });
  }

  async #refreshMarqueeAfterLayout() {
    if (this.hasAttribute('data-no-repeat')) return;
    if (this.#hasSsrLoop()) return;

    await new Promise((r) => {
      requestAnimationFrame(() => requestAnimationFrame(r));
    });

    if (this.hasAttribute('data-disabled')) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();
    const current = this.refs.content.children.length;

    if (numberOfCopies > current) {
      this.#addRepeatedItems(numberOfCopies - current);
    } else if (numberOfCopies < current) {
      this.#removeRepeatedItems(current - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed());
    this.#restartAnimation();
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const laneWidth = this.#getScrollLaneReferenceWidth();
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== laneWidth;
          this.#marqueeWidth = laneWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies:
                marqueeItemsWidth === 0 ? 1 : Math.ceil(laneWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * Split layout: scroll lane width (marquee minus static overlays).
   * @returns {number}
   */
  #getScrollLaneReferenceWidth() {
    const marqueeWidth = this.getBoundingClientRect().width;
    if (!this.classList.contains('marquee-layout--split')) {
      return marqueeWidth;
    }

    if (!this.#overlays) {
      this.#overlays = this.querySelectorAll('.marquee__static-overlay');
    }

    let overlayWidth = 0;
    this.#overlays.forEach((overlay) => {
      overlayWidth += overlay.getBoundingClientRect().width;
    });

    return Math.max(0, marqueeWidth - overlayWidth);
  }

  /** Duration (seconds) for one loop — same formula as base marquee.js. */
  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const copyCount = Math.max(1, this.refs.content?.children.length ?? 1);
    return Math.sqrt(copyCount) * speedFactor;
  }

  #handleResize = debounce(async () => {
    // If user explicitly disabled autoplay via setting, never auto-enable
    if (this.hasAttribute('data-user-disabled')) return;

    const isNoRepeat = this.hasAttribute('data-no-repeat');

    if (this.#isAlwaysLoopTrack()) {
      this.removeAttribute('data-disabled');
      this.#setSpeed(this.#calculateSpeed());
      return;
    }

    // Default: stop when items fit the container.
    const contentFits = await this.#checkContentFits();
    const currentlyDisabled = this.hasAttribute('data-disabled');

    if (contentFits && !currentlyDisabled) {
      // Content now fits — stop scrolling
      this.setAttribute('data-disabled', '');
      this.removeAttribute('data-scrolling');
      this.#setSpeed(0);
      this.clonedContent?.remove();
      this.removeEventListener('pointerenter', this.#slowDown);
      this.removeEventListener('pointerleave', this.#speedUp);
      return;
    }

    if (!contentFits && currentlyDisabled) {
      // Content now overflows — start scrolling
      this.removeAttribute('data-disabled');
      const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();
      if (!isHorizontalResize) {
        return;
      }
      if (!isNoRepeat) {
        const current = this.refs.content.children.length;
        if (numberOfCopies > current) {
          this.#addRepeatedItems(numberOfCopies - current);
        } else if (numberOfCopies < current) {
          this.#removeRepeatedItems(current - numberOfCopies);
        }
        this.#duplicateContent();
      }
      this.setAttribute('data-scrolling', '');
      this.#setSpeed(this.#calculateSpeed());
      this.addEventListener('pointerenter', this.#slowDown);
      this.addEventListener('pointerleave', this.#speedUp);
      if (!isNoRepeat) {
        await this.#refreshMarqueeAfterLayout();
      }
      return;
    }

    if (!contentFits) {
      // Already scrolling — adjust copies/speed for new viewport
      const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();
      if (!isHorizontalResize) {
        return;
      }
      if (!isNoRepeat) {
        const currentNumberOfCopies = this.refs.content.children.length;
        if (numberOfCopies > currentNumberOfCopies) {
          this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
        } else if (numberOfCopies < currentNumberOfCopies) {
          this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
        }
        this.#duplicateContent();
      }
      this.#setSpeed(this.#calculateSpeed());
      this.#restartAnimation();
    }
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    if (this.#hasSsrLoop()) return;

    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('hw-marquee')) {
  customElements.define('hw-marquee', HWMarqueeComponent);
}